import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Monitor,
  Users,
  Building2,
  Settings,
  LogOut,
  Download,
  History,
  Menu,
  X,
  Bell,
} from 'lucide-react'
import { useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useSocketStore } from '@/stores/socketStore'
import { cn, getInitials } from '@/lib/utils'
import Logo from './Logo'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Devices', href: '/devices', icon: Monitor },
  { name: 'Sessions', href: '/sessions', icon: History },
  { name: 'Customers', href: '/customers', icon: Building2 },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Downloads', href: '/downloads', icon: Download },
  { name: 'Settings', href: '/settings', icon: Settings },
]

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const socketConnected = useSocketStore((s) => s.connected)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-sc-bg-darkest flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 bg-sc-bg-darker border-r border-sc-border-dark transform transition-transform duration-200 lg:transform-none',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo + live indicator */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-sc-border-dark">
            <div className="flex items-center gap-2">
              <Logo />
              <span
                title={socketConnected ? 'Live — receiving real-time updates' : 'Connecting…'}
                className={cn(
                  'w-2 h-2 rounded-full flex-shrink-0 transition-colors',
                  socketConnected ? 'bg-sc-accent-green animate-pulse' : 'bg-sc-text-muted'
                )}
              />
            </div>
            <button
              className="lg:hidden p-2 text-sc-text-muted hover:text-sc-text-primary"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn('sidebar-link', isActive && 'active')
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-sc-border-dark">
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="w-9 h-9 rounded-full bg-sc-primary/20 flex items-center justify-center text-sc-primary font-medium text-sm">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  getInitials(user?.name || 'U')
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sc-text-primary truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-sc-text-muted truncate capitalize">
                  {user?.role}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-sc-text-muted hover:text-sc-accent-red transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 bg-sc-bg-darker border-b border-sc-border-dark flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden p-2 text-sc-text-muted hover:text-sc-text-primary"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="hidden md:flex items-center gap-3 min-w-0">
              <Logo size="sm" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-sc-text-primary truncate">PitStop Portal</p>
                <p className="text-xs text-sc-text-muted truncate">Sim Coaches remote support operations</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/settings?tab=notifications')}
              className="p-2 text-sc-text-muted hover:text-sc-text-primary relative"
              title="Notification settings"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-sc-accent-red rounded-full" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
